import Vue from 'vue';
import VueRouter from 'vue-router';
import Layout from '@/layout/index.vue';
import store from '@/store';
import { getToken } from '@/utils/auth';
import { getStoreId } from '@/utils/business-context';

Vue.use(VueRouter);

export const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录', public: true, hidden: true }
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '页面不存在', public: true, hidden: true }
  },
  {
    path: '/download',
    name: 'SoftwareDownload',
    component: () => import('@/views/download/index.vue'),
    meta: { title: '下载桌面版', public: true, hidden: true }
  },
  {
    path: '/ks_auth',
    name: 'KuaishouAuthCallback',
    component: () => import('@/views/auth/kuaishou.vue'),
    meta: { title: '快手授权', public: true, hidden: true }
  }
];

export const asyncRoutes = [
  {
    path: '/',
    redirect: '/home',
    meta: { hidden: true }
  },
  {
    path: '/home',
    component: Layout,
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/home/index.vue'),
        meta: { title: '工作台', icon: 'el-icon-odometer', affix: true }
      }
    ]
  },
  {
    path: '/content/profile',
    component: Layout,
    children: [{ path: '', name: 'BusinessProfile', component: () => import('@/views/content/profile.vue'), meta: { title: '生意档案库', icon: 'el-icon-document' } }]
  },
  {
    path: '/content/media',
    component: Layout,
    children: [{ path: '', name: 'MediaLibrary', component: () => import('@/views/content/media.vue'), meta: { title: '视频素材库', icon: 'el-icon-picture-outline' } }]
  },
  {
    path: '/content/editor',
    component: Layout,
    children: [{ path: '', name: 'CloudEditor', component: () => import('@/views/content/editor.vue'), meta: { title: '智能云剪辑', icon: 'el-icon-scissors' } }]
  },
  {
    path: '/content/works',
    component: Layout,
    children: [{ path: '', name: 'WorksLibrary', component: () => import('@/views/content/works.vue'), meta: { title: '我的视频库', icon: 'el-icon-film' } }]
  },

  {
    path: '/matrix/accounts',
    component: Layout,
    children: [{ path: '', name: 'MatrixAccounts', component: () => import('@/views/matrix/accounts.vue'), meta: { title: '矩阵账号', icon: 'el-icon-user' } }]
  },
  {
    path: '/matrix/accounts/desktop',
    component: Layout,
    meta: { hidden: true },
    children: [{ path: '', name: 'MatrixDesktop', component: () => import('@/views/matrix/desktop.vue'), meta: { title: '桌面端托管', hidden: true } }]
  },
  {
    path: '/matrix/tasks',
    component: Layout,
    children: [{ path: '', name: 'MatrixTasks', component: () => import('@/views/matrix/tasks.vue'), meta: { title: '矩阵发布', icon: 'el-icon-s-promotion' } }]
  },
  {
    path: '/matrix/tasks/create',
    component: Layout,
    meta: { hidden: true },
    children: [{ path: '', name: 'MatrixTaskCreate', component: () => import('@/views/matrix/task-editor.vue'), meta: { title: '创建矩阵任务', hidden: true } }]
  },
  {
    path: '/matrix/tasks/batch-create',
    component: Layout,
    meta: { hidden: true },
    children: [{ path: '', name: 'MatrixBatchCreate', component: () => import('@/views/matrix/batch-create.vue'), meta: { title: '批量创建矩阵任务', hidden: true } }]
  },
  {
    path: '/content/stores',
    component: Layout,
    children: [{ path: '', name: 'StoreProjects', component: () => import('@/views/content/stores.vue'), meta: { title: '账号信息', icon: 'el-icon-folder-opened' } }]
  },
  {
    path: '/tutorial',
    component: Layout,
    children: [{ path: '', name: 'BeginnerTutorial', component: () => import('@/views/tutorial/index.vue'), meta: { title: '新手教程', icon: 'el-icon-reading' } }]
  }
];

const router = new VueRouter({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  routes: [...constantRoutes, ...asyncRoutes, { path: '*', redirect: '/404', meta: { hidden: true } }]
});

router.beforeEach(async (to, from, next) => {
  store.commit('app/SET_GLOBAL_LOADING', true);
  if (to.name === 'KuaishouAuthCallback') return next();

  const token = getToken();

  if (!token) {
    if (to.meta.public) return next();
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  if (to.path === '/login') return next('/home');

  if (!store.state.user.userInfo) {
    try {
      await store.dispatch('user/getInfo');
    } catch (error) {
      await store.dispatch('user/resetToken');
      return next({ path: '/login', query: { redirect: to.fullPath } });
    }
  }

  if (!getStoreId() && from.name && from.path !== '/login' && to.path !== from.path) {
    store.commit('app/SET_GLOBAL_LOADING', false);
    window.dispatchEvent(new CustomEvent('open-store-setup'));
    return next(false);
  }

  const roles = store.state.user.roles;
  const requiredRoles = to.matched.reduce((items, record) => items.concat(record.meta.roles || []), []);
  if (requiredRoles.length && !requiredRoles.some((role) => roles.includes(role))) return next('/404');

  next();
});

router.afterEach((to) => {
  store.commit('app/SET_GLOBAL_LOADING', false);
  document.title = to.meta.title ? `${to.meta.title} - ${process.env.VUE_APP_TITLE}` : process.env.VUE_APP_TITLE;
});

export default router;
